// Downloads Grid Component
// Grid of DownloadCards - receives admin prop, passes to cards
// Empty state until API provides downloads

import React, { useState, useRef, useCallback } from 'react';
import { DownloadCard } from './DownloadCard';
import './DownloadsGrid.css';

export function DownloadsGrid({ downloads = [], admin = false, loading = false, emptyMessage = 'No downloads found', sortMode = '', onDownloadClick, onPinToggle, onEdit, onDelete, onReorder }) {
  const [draggedDownload, setDraggedDownload] = useState(null);
  const [dragOverDownload, setDragOverDownload] = useState(null);
  const didDragRef = useRef(false);

  const handleDragStart = useCallback((e, download) => {
    setDraggedDownload(download);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, download) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDownload(download);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverDownload(null);
  }, []);

  const handleDrop = useCallback((e, targetDownload) => {
    e.preventDefault();
    setDragOverDownload(null);

    if (!draggedDownload || !targetDownload || draggedDownload.id === targetDownload.id) {
      setDraggedDownload(null);
      return;
    }

    if (!onReorder) {
      setDraggedDownload(null);
      return;
    }

    // Only allow reordering if both items are pinned
    if (!draggedDownload.isPinned || !targetDownload.isPinned) {
      setDraggedDownload(null);
      return;
    }

    const swaps = [
      { downloadId: draggedDownload.id, pinnedOrder: targetDownload.pinnedOrder },
      { downloadId: targetDownload.id, pinnedOrder: draggedDownload.pinnedOrder },
    ];

    didDragRef.current = true;
    onReorder(swaps);
    setDraggedDownload(null);
  }, [draggedDownload, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDraggedDownload(null);
    setDragOverDownload(null);
  }, []);

  const wrappedOnDownloadClick = useCallback((download) => {
    if (admin && didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    if (onDownloadClick) {
      onDownloadClick(download);
    }
  }, [admin, onDownloadClick]);
  if (loading) {
    return (
      <div className="downloads-grid downloads-grid--loading">
        <div className="downloads-grid-loading">
          <i className="fa-solid fa-spinner fa-spin" aria-hidden></i>
          <p>Loading downloads...</p>
        </div>
      </div>
    );
  }

  if (!downloads || downloads.length === 0) {
    return (
      <div className="downloads-grid">
        <div className="downloads-grid-empty">
          <i className="fa-solid fa-inbox" aria-hidden></i>
          <h3>No downloads found</h3>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="downloads-grid">
      {downloads.map((download) => (
        <DownloadCard
          key={download.id}
          download={download}
          admin={admin}
          sortMode={sortMode}
          onClick={wrappedOnDownloadClick}
          onPinToggle={onPinToggle}
          onEdit={onEdit}
          onDelete={onDelete}
          draggable={admin && sortMode === ''} // Only enable drag when in admin mode and default sort
          isDragging={draggedDownload?.id === download.id}
          isDragOver={dragOverDownload?.id === download.id}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
        />
      ))}
    </div>
  );
}
